import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentTokenUsage } from '../services/tokenUsageService';
import { useSubscription } from './useSubscription';

/**
 * Hook to protect routes from direct URL access when credits are exhausted.
 * Checks token limits on mount and shows subscription modal if credits are exhausted.
 * 
 * Usage: Add `useProtectedRoute()` at the top of any protected page component.
 */
export const useProtectedRoute = () => {
    const navigate = useNavigate();
    const { hasActiveSubscription, openSubscriptionModal } = useSubscription();

    useEffect(() => {
        const checkAccess = async () => {
            // If user has active subscription, allow access
            if (hasActiveSubscription) {
                return;
            }

            // For non-subscribed users, check token limits
            try {
                const usage = await getCurrentTokenUsage();
                if (usage.isLimitExceeded) {
                    // Credits exhausted - show subscription modal and redirect to dashboard
                    openSubscriptionModal();
                    navigate('/dashboard', { replace: true });
                }
                // Credits available - allow access
            } catch (error) {
                console.error('Failed to check token usage on route access:', error);
                // On error, allow access (don't block user)
            }
        };

        checkAccess();
    }, [hasActiveSubscription, openSubscriptionModal, navigate]);
};
